/**
 * Pure helpers for NxAutocompleteInput commit-on-Enter / blur.
 * Extracted so unit tests can cover id+Enter without mounting the full input.
 */

export type AutocompleteOption = { name: string; value: number };

/** True when `raw` is a pure integer string (e.g. "38"), not "38.5" or "038". */
export function isPureNumericQuery(raw: string): boolean {
  return raw !== '' && Number(raw).toFixed() === raw;
}

/**
 * Which option should Enter / exact-match blur commit?
 * - highlighted dropdown row
 * - pure numeric query matching an option id (type `38` + Enter) — unless
 *   `requireHighlight` (Artikel Schnellsuche: Enter only with a selected row)
 * - single remaining result after a search — unless `requireHighlight`
 */
export function resolveCommitOption(args: {
  options: Map<number, string>;
  relevantOptions: readonly AutocompleteOption[];
  selectedOptionIndex: number;
  shadowValue: string;
  searchIgnoreId?: boolean;
  /** When false, do not auto-commit a single remaining search hit (blur path). */
  allowSoleHit?: boolean;
  /** When true, only the highlighted dropdown row may commit (no id / sole-hit). */
  requireHighlight?: boolean;
}): AutocompleteOption | null {
  const {
    options,
    relevantOptions,
    selectedOptionIndex,
    shadowValue,
    searchIgnoreId = false,
    allowSoleHit = true,
    requireHighlight = false,
  } = args;

  const highlighted = selectedOptionIndex > -1 ? relevantOptions[selectedOptionIndex] : undefined;
  if (highlighted) return highlighted;
  if (requireHighlight) return null;

  const raw = String(shadowValue).trim();
  if (!searchIgnoreId && isPureNumericQuery(raw)) {
    const id = Number(raw);
    // Use has() — get() is falsy for empty-string / 0 labels but the id is valid.
    if (options.has(id)) {
      return { name: String(options.get(id) ?? ''), value: id };
    }
  }

  if (allowSoleHit && relevantOptions.length === 1) {
    return relevantOptions[0] ?? null;
  }
  return null;
}
